import { useEffect, useState, useCallback } from "react";
import { getQueue, flushQueue } from "@/lib/offlineStore";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useOnline — React hook som overvåker nettverksstatus + kø-status.
 *
 * Returnerer { online, pendingCount, syncing, syncNow }.
 * Ved online-transisjon spyles køen automatisk.
 */
export function useOnline() {
  const [online, setOnline] = useState(() => (typeof navigator !== "undefined" ? navigator.onLine : true));
  const [pendingCount, setPendingCount] = useState(() => getQueue().length);
  const [syncing, setSyncing] = useState(false);

  const syncNow = useCallback(async () => {
    if (syncing) return;
    const q = getQueue();
    if (q.length === 0) return;
    setSyncing(true);
    try {
      const res = await flushQueue(async ({ method, url, data }) => {
        const token = localStorage.getItem("token");
        return axios({
          method,
          url: `${API}${url}`,
          data,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      });
      setPendingCount(res.remaining);
      if (res.flushed > 0) {
        toast.success(`${res.flushed} endring${res.flushed === 1 ? "" : "ar"} synkronisert`);
      }
      if (res.errors.length > 0 && res.errors.some((e) => e.error === "network")) {
        toast.error("Nettverket er utilgjengeleg — prøver igjen seinare");
      } else if (res.errors.length > 0) {
        toast.error(`${res.errors.length} endring${res.errors.length === 1 ? "" : "ar"} feila (sjå konsoll for detaljar)`);
        console.warn("[offline sync] failed:", res.errors);
      }
      // Trigger UI refresh across app
      window.dispatchEvent(new CustomEvent("pb-synced"));
    } finally {
      setSyncing(false);
    }
  }, [syncing]);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      // Spyl køen straks vi er online igjen
      syncNow();
    };
    const handleOffline = () => setOnline(false);
    const handleQueueChange = (e) => setPendingCount(e.detail?.count ?? getQueue().length);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("pb-queue-changed", handleQueueChange);

    // Første kø-flush hvis vi allereie er online + noe ventar
    if (online && getQueue().length > 0) {
      syncNow();
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("pb-queue-changed", handleQueueChange);
    };
  }, []); // effect kun ved mount — syncNow re-genererer state kun ved bruk

  return { online, pendingCount, syncing, syncNow };
}
